/* eslint-disable react-hooks/exhaustive-deps */
import { ChartBarIcon, PlusCircleIcon } from "@heroicons/react/20/solid";
import {
  Button,
  Flex, Grid, LoadingOverlay, Paper, Select, Text,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useMutation } from "@tanstack/react-query";
import { subDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import uuid from "react-uuid";
import { queryClient, useWorkspaceObjectSchema } from "../../../../../hooks/QueryHooks";
import { createMetric, deleteMetric, updateMetric } from "../../../../../services/metric/metricEndpoint";
import { updateWorkspacePreference } from "../../../../../services/preference/preferenceEndpoints";
import { getUserMetricsGraph } from "../../../../../services/userActivityEndpoint";
import dateConverter from "../../../../../utils/dateConverter";
import notify from "../../../../../utils/notify";
import CustomChart from "../../account-overview/components/CustomChart";
import NewTrendModal from "../../account-overview/components/NewTrendModal";

type Props = {
  account: any;
  prefs: any;
  metrics: any;
  currentWorkspace: any;
};

const UserTrendsContainer = ({
  account, currentWorkspace, prefs, metrics,
}: Props) => {
  const [opened, { close, open }] = useDisclosure(false);
  const [loading, setLoading] = useState(false);

  // Keep the mutation for use in the onChange handlers
  const updatePrefMutation: any = useMutation({
    mutationFn: updateWorkspacePreference,
    onSuccess: () => {
      queryClient.invalidateQueries(["workspace_preferences", currentWorkspace.id]);
    },
  });

  const deleteMetricMutation: any = useMutation({
    mutationFn: deleteMetric,
  });

  // Initialize state with values from preferences or defaults
  // Using the prefs directly in the initial state to avoid flashing of default values
  const [bucket, setBucket] = useState<any>(
    prefs?.data?.users?.metrics?.grouping || "day"
  );

  const [duration, setDuration] = useState<any>(
    prefs?.data?.users?.metrics?.time_window || "last_7_days"
  );
  const [tableData, setTableData] = useState<any>({});

  // Effect to update state when preferences change
  // This ensures values are preserved when switching between views
  useEffect(() => {
    if (prefs?.data?.users?.metrics) {
      const prefBucket = prefs.data.users.metrics.grouping;
      const prefTimeWindow = prefs.data.users.metrics.time_window;

      // Only update local state if it's different from preferences
      // This prevents unnecessary re-renders
      if (prefBucket && prefBucket !== bucket) {
        setBucket(prefBucket);
      }

      if (prefTimeWindow && prefTimeWindow !== duration) {
        setDuration(prefTimeWindow);
      }
    }
  }, [prefs?.data?.users?.metrics]); // Only depend on the preferences data

  // Remove the useEffect that was causing infinite calls
  // We'll update preferences directly in the onChange handlers instead

  const bucketList = useMemo(() => (
    [{ value: "day", label: "Daily" },
    { value: "week", label: "Weekly" },
    { value: "month", label: "Monthly" },
    ]
  ), []);

  const durationList = useMemo(() => {
    if (bucket === "day") {
      return [
        { value: "last_7_days", label: "Last 7 days" },
        { value: "last_30_days", label: "Last 30 days" },
        { value: "last_90_days", label: "Last 90 days" },
        { value: "last_180_days", label: "Last 180 days" },
      ];
    } if (bucket === "week") {
      return [
        { value: "last_4_weeks", label: "Last 4 weeks" },
        { value: "last_8_weeks", label: "Last 8 weeks" },
        { value: "last_12_weeks", label: "Last 12 weeks" },
        { value: "last_24_weeks", label: "Last 24 weeks" },
      ];
    } if (bucket === "month") {
      return [
        { value: "last_3_months", label: "Last 3 months" },
        { value: "last_6_months", label: "Last 6 months" },
        { value: "last_9_months", label: "Last 9 months" },
        { value: "last_12_months", label: "Last 12 months" },
      ];
    } return [];
  }, [bucket]);

  const onDeleteClick = (id: any) => {
    deleteMetricMutation.mutate({
      workspace_id: currentWorkspace.id,
      metric_id: id,
    }, {
      onSuccess: (data: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace.id], (oldData: any) => ({
          ...oldData,
          data: oldData.data.filter((item: any) => item.id !== data.data.id),
        }));
        if (import.meta.env.VITE_HE_WORKSPACE_KEY && import.meta.env.VITE_HE_DATA_KEY) {
          window?.hyperengage(
            "track",
            "deleted_pinned_metric",
            {
              properties: {
                metric_id: id,
                workspace_id: currentWorkspace.id,
                entity_type: "users",
              },
            },
          );
        }
        notify({
          type: "success",
          messageList: ["Removed Pinned metric"],
        });
      },
      onFailure: () => {
        notify({
          type: "failure",
          messageList: ["Failed to remove Pinned metric"],
        });
      },
    });
  };

  const editTitleMutation: any = useMutation({
    mutationFn: (params: any) => updateMetric(params),
    onSuccess: (data: any) => {
      queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace.id], (oldData: any) => {
        const updatedData = oldData.data.map((item: any) => {
          if (item.id === data.data.id) {
            return data.data;
          }
          return item;
        });
        return {
          ...oldData,
          data: updatedData,
        };
      });
      notify({
        type: "success",
        messageList: ["Updated pinned metric"],
      });
    },
    onError: () => {
      notify({
        type: "failure",
        messageList: ["Failed to update pinned metric"],
      });
    },
  });

  const onEditTitle = (id: string, newTitle: string) => {
    const metric = metrics?.find((m: any) => m.id === id);
    if (!metric) return;

    editTitleMutation.mutate({
      workspace_id: currentWorkspace?.id,
      metric_id: id,
      display_name: newTitle,
      description: metric.description || "",
      type: metric.type,
      linked_entity: "users",
    });
  };

  const updateMetricMutation: any = useMutation({
    mutationFn: (params: any) => updateMetric(params),
    onSuccess: (data: any) => {
      queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace.id], (oldData: any) => {
        const updatedData = oldData.data.map((item: any) => {
          if (item.id === data.data.id) {
            return data.data;
          }
          return item;
        });
        return {
          ...oldData,
          data: updatedData,
        };
      });
      notify({
        type: "success",
        messageList: ["Updated pinned metric"],
      });
    },
    onError: () => {
      notify({
        type: "failure",
        messageList: ["Failed to update pinned metric"],
      });
    },
  });

  const onUpdateMetric = (id: string, newMetric: string, newTitle: string, newChartType: string) => {
    const metric = metrics?.find((m: any) => m.id === id);
    if (!metric) return;

    updateMetricMutation.mutate({
      workspace_id: currentWorkspace?.id,
      metric_id: id,
      aggregator: newMetric,
      display_name: newTitle,
      description: metric.description || "",
      type: newChartType,
      linked_entity: "users",
    });
  };

  useEffect(() => {
    const getMetrics = async () => {
      setLoading(true);
      let days = 7;
      // Extract the numeric part from the time window string
      const durationMatch = duration.match(/\d+/);
      const durationNumber = durationMatch ? parseInt(durationMatch[0], 10) : 7;

      if (bucket === "day" || duration.includes('days')) {
        days = durationNumber;
      } else if (bucket === "week" || duration.includes('weeks')) {
        days = durationNumber * 7;
      } else if (bucket === "month" || duration.includes('months')) {
        days = durationNumber * 30;
      }
      try {
        // Only fetch metrics for user view
        if (metrics?.length > 0 && account?.external_id) {
          // Ensure metrics is an array before mapping
          const apiPromises = (metrics || [])?.map(async (event: any) => {
            const response = await getUserMetricsGraph({
              bucket_size: bucket,
              external_id: account?.external_id,
              event_name: event.aggregator,
              workspace_identifier: currentWorkspace?.id,
              from_date: dateConverter(subDays(new Date(), days)),
            });
            return response;
          });
          const resolvedPromises = await Promise.all(apiPromises);
          const newTableData = {};
          resolvedPromises.forEach((response: any) => {
            if (response?.data) {
              Object.assign(newTableData, response.data);
            }
          });
          setTableData(newTableData);
        }
      } catch (error) {
        setTableData({});
      }
      setLoading(false);
    };
    if (!!account?.external_id && !!currentWorkspace?.id && metrics?.length > 0) {
      getMetrics();
    } else {
      setTableData({});
    }
  }, [account?.external_id, bucket, duration, currentWorkspace?.id, metrics?.length]);

  const {
    data: workspaceSchema,
  }: any = useWorkspaceObjectSchema({
    workspaceId: currentWorkspace?.id,
    enabled: !!currentWorkspace?.id,
  });
  const userMetrics = workspaceSchema?.metricsByObjectSlug?.users
    || workspaceSchema?.metrics
    || { list: [], obj: {} };

  const selectedEvents = useMemo(() => metrics
    ?.map((item: any) => item.aggregator) || [], [metrics]);

  const createMetricMutation: any = useMutation({
    mutationFn: createMetric,
  });

  const shownList = useMemo(() => (
    userMetrics?.list?.filter((item: any) => !selectedEvents?.find(
      (event: any) => event === item.name,
    ))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [selectedEvents, userMetrics?.list]);

  const shownTraitsList = useMemo(
    () => shownList?.filter((item: any) => item.attribute_type === "traits") || [],
    [shownList],
  );
  const shownSuccessMetricsList = useMemo(
    () => shownList?.filter((item: any) => item.attribute_type === "computed_traits") || [],
    [shownList],
  );
  const shownMetricsList = useMemo(
    () => shownList?.filter((item: any) => item.attribute_type === "metrics") || [],
    [shownList],
  );

  const onMetricSubmit = (metric: string, metricName: string, type: string) => {
    createMetricMutation.mutate({
      workspace_id: currentWorkspace?.id,
      aggregator: metric,
      display_name: metricName,
      description: "",
      type,
      id: `am_${metric}_${Math.random() * 1000}`,
      linked_entity: "users",
      is_default: false,
    }, {
      onSuccess: (newMetric: any) => {
        queryClient.setQueryData(["workspace_metric_preferences", currentWorkspace?.id], (oldData: any) => ({
          ...oldData,
          data: [...oldData.data, newMetric.data],
        }));
        if (import.meta.env.VITE_HE_WORKSPACE_KEY && import.meta.env.VITE_HE_DATA_KEY) {
          window?.hyperengage(
            "track",
            "created_pinned_metric",
            {
              properties: {
                display_name: metricName,
                aggregator: metric,
                workspace_id: currentWorkspace.id,
                entity_type: "users",
              },
            },
          );
        }
        notify({
          type: "success",
          messageList: ["Pinned metric successfully"],
        });
        close();
      },
      onFailure: () => {
        notify({
          type: "failure",
          messageList: ["Something went wrong"],
        });
      },
    });
  };

  return (
    <div className="w-full border bg-white border-gray-200 p-5">
      <NewTrendModal
        onMetricSubmit={onMetricSubmit}
        traitsList={shownTraitsList}
        successMetricsList={shownSuccessMetricsList}
        metricsList={shownMetricsList}
        opened={opened}
        close={close}
      />
      <Flex justify="space-between" align="center" mb="md">
        <Text className="flex gap-2 items-center" fz={20} fw={600} c="text.9">
          <ChartBarIcon className="w-4 h-4 fill-purple-500" />
          Trends
        </Text>
        <Flex gap={10} align="center">
          <Select
            value={bucket}
            onChange={(value) => {
              if (!value) return;
              setBucket(value);
              let dur;
              if (value === "day") {
                dur = "last_7_days";
              } else if (value === "week") {
                dur = "last_4_weeks";
              } else if (value === "month") {
                dur = "last_3_months";
              } else {
                dur = duration; // Fallback to current duration if needed
              }
              setDuration(dur);

              if (prefs?.data && (value !== prefs?.data?.users?.metrics?.grouping || dur !== prefs?.data?.users?.metrics?.time_window)) {
                updatePrefMutation.mutate({
                  workspace_id: currentWorkspace.id,
                  users: {
                    ...prefs?.data?.users,
                    metrics: {
                      grouping: value,
                      time_window: dur,
                    },
                  },
                  accounts: prefs?.data?.accounts,
                  updated_by: prefs?.data?.updated_by,
                  id: prefs?.data?.id,
                });
              }
            }}
            classNames={{
              input: "rounded-md w-[150px]",
              root: "focus:ring-purple-500",
            }}
            placeholder="Select range"
            data={bucketList}
          />
          <Select
            value={duration}
            onChange={(value) => {
              if (value) {
                setDuration(value);
                if (prefs?.data && value !== prefs?.data?.users?.metrics?.time_window) {
                  updatePrefMutation.mutate({
                    workspace_id: currentWorkspace.id,
                    users: {
                      ...prefs?.data?.users,
                      metrics: {
                        grouping: bucket,
                        time_window: value,
                      },
                    },
                    accounts: prefs?.data?.accounts,
                    updated_by: prefs?.data?.updated_by,
                    id: prefs?.data?.id,
                  });
                }
              }
            }}
            classNames={{
              input: "rounded-md w-[150px]",
              root: "focus:ring-purple-500",
            }}
            placeholder="Select range"
            data={durationList}
          />
          <div className="relative group">
            <Button
              disabled={metrics?.length >= 8}
              onClick={open}
              className="text-sm"
              color="white"
              size="sm"
              leftSection={<PlusCircleIcon className="w-5 h-5" />}
            >
              New pinned Metric
            </Button>
            {metrics?.length >= 8 && (
              <div className="absolute right-0 mt-1 w-60 bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 pointer-events-none text-center">
                Maximum pinned metrics reached. Remove some to add more.
              </div>
            )}
          </div>
        </Flex>
      </Flex>
      <div className="w-full mt-5">
        <Grid className="w-full min-h-[230px] relative">
          <LoadingOverlay loaderProps={{ size: "md" }} c="main.5" visible={loading} overlayProps={{ blur: 2 }} />
          {metrics?.length !== 0 ? (
            // Display all metrics regardless of whether we have data for them
            (metrics || []).map((metric: any) => {
              const data = tableData[metric.aggregator] || { total_count: 0, percentage_change: 0, histogram_values: [] };
              return (
                <Grid.Col key={uuid()} span={4}>
                  <CustomChart
                    title={metric.display_name}
                    data={data}
                    type={metric.type}
                    deleteHandler={onDeleteClick}
                    editTitleHandler={onEditTitle}
                    updateMetricHandler={onUpdateMetric}
                    metricsList={userMetrics?.list || []}
                    aggregator={metric.aggregator}
                    id={metric.id}
                  />
                </Grid.Col>
              );
            })
          ) : (
            <Paper p="xl" radius="md" className="bg-gray-50 italic text-gray-500 w-full flex items-center justify-center" shadow="sm">
              <Text size="sm" fw={500}>
                Add your first custom success metric and generate graphs to view trends.
              </Text>
            </Paper>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default UserTrendsContainer;
